"use strict";
import { memo, useCallback, useState } from "react";
import { Flex, Text, TouchableArea } from "ui/src";
import { Check } from "ui/src/components/icons/Check";
import { TokenLogo } from "components/CurrencyLogo/TokenLogo";
import { OptionItem } from "components/lists/items/OptionItem";
import {
  TokenContextMenuAction,
  TokenOptionItemContextMenu
} from "components/lists/items/tokens/TokenOptionItemContextMenu";
import { WarningSeverity } from "components/modals/WarningModal/types";
import WarningIcon from "components/warnings/WarningIcon";
import { getWarningIconColors } from "components/warnings/utils";
import { isTokenVerified } from "constants/topazTokenList";
import TokenWarningModal from "features/tokens/TokenWarningModal";
import { getTokenWarningSeverity } from "features/tokens/safetyUtils";
import { getSymbolDisplayText } from "utils/currency";
import { shortenAddress } from "utilities/src/addresses";
import { dismissNativeKeyboard } from "utilities/src/device/keyboard/dismissNativeKeyboard";
import { isInterface, isWeb } from "utilities/src/platform";
import { useBooleanState } from "utilities/src/react/useBooleanState";
import { BlueBadgeVerified } from "components/BadgeProvider";
export var TokenContextMenuVariant = /* @__PURE__ */ ((TokenContextMenuVariant2) => {
  TokenContextMenuVariant2["Search"] = "search";
  TokenContextMenuVariant2["TokenSelector"] = "tokenSelector";
  return TokenContextMenuVariant2;
})(TokenContextMenuVariant || {});
const CONTEXT_MENU_ACTIONS = {
  ["search" /* Search */]: [
    TokenContextMenuAction.CopyAddress,
    ...isWeb ? [] : [TokenContextMenuAction.Favorite],
    TokenContextMenuAction.Swap,
    TokenContextMenuAction.Send,
    TokenContextMenuAction.Receive,
    TokenContextMenuAction.Share
  ],
  ["tokenSelector" /* TokenSelector */]: [
    TokenContextMenuAction.CopyAddress,
    ...isWeb ? [] : [TokenContextMenuAction.Favorite],
    TokenContextMenuAction.ViewDetails
  ]
};
const LegacyBaseTokenOptionItem = memo(function LegacyBaseTokenOptionItem2({
  option,
  showTokenAddress,
  balance,
  quantity,
  quantityFormatted,
  isSelected
}) {
  const { currencyInfo } = option;
  const { currency } = currencyInfo;
  const severity = getTokenWarningSeverity(currencyInfo);
  const { colorSecondary: warningIconColor } = getWarningIconColors(severity);
  return <Flex
    row
    alignItems="center"
    gap="$spacing8"
    justifyContent="space-between"
    px="$spacing16"
    py="$spacing12"
    style={{
      pointerEvents: "auto"
    }}
    testID={`token-option-${currency.chainId}-${currency.symbol}`}
  >
      <Flex row shrink alignItems="center" gap="$spacing12">
        <TokenLogo
    chainId={currency.chainId}
    name={currency.name}
    symbol={currency.symbol}
    url={currencyInfo.logoUrl ?? void 0}
  />
        <Flex shrink>
          <Flex row alignItems="center" gap="$spacing8">
            <Text color="$neutral1" numberOfLines={1} variant="body1">
              {currency.name}
            </Text>
            {isTokenVerified(currency) && <BlueBadgeVerified size={"$icon.16"} />}
            {warningIconColor && <Flex>
                <WarningIcon severity={severity} size="$icon.16" strokeColorOverride={warningIconColor} />
              </Flex>}
          </Flex>
          <Flex row alignItems="center" gap="$spacing8">
            <Text color="$neutral2" numberOfLines={1} variant="body3">
              {getSymbolDisplayText(currency.symbol)}
            </Text>
            {!currency.isNative && showTokenAddress && <Flex shrink>
                <Text color="$neutral3" numberOfLines={1} variant="body3">
                  {shortenAddress(currency.address)}
                </Text>
              </Flex>}
          </Flex>
        </Flex>
      </Flex>

      {isSelected && <Flex grow alignItems="flex-end" justifyContent="center">
          <Check color="$neutral2Temp" size="$icon.20" />
        </Flex>}

      {!isSelected && quantity && quantity !== 0 ? <Flex alignItems="flex-end">
          <Text variant="body1">{balance}</Text>
          {quantityFormatted && <Text color="$neutral2" variant="body3">
              {quantityFormatted}
            </Text>}
        </Flex> : null}
    </Flex>;
});
function _LegacyTokenOptionItem(props) {
  const { option, showWarnings, onPress, tokenWarningDismissed, isKeyboardOpen } = props;
  const { currencyInfo, isUnsupported } = option;
  const { currency } = currencyInfo;
  const [showWarningModal, setShowWarningModal] = useState(false);
  const severity = getTokenWarningSeverity(currencyInfo);
  const isBlocked = severity === WarningSeverity.Blocked;
  const shouldShowWarningModalOnPress = isBlocked || severity !== WarningSeverity.None && !tokenWarningDismissed;
  const handleShowWarningModal = useCallback(() => {
    dismissNativeKeyboard();
    setShowWarningModal(true);
  }, [setShowWarningModal]);
  const { value: isContextMenuOpen, setFalse: closeContextMenu, setTrue: openContextMenu } = useBooleanState(false);
  const onPressTokenOption = useCallback(() => {
    if (showWarnings && shouldShowWarningModalOnPress) {
      if (isKeyboardOpen && isInterface) {
        const activeElement = document.activeElement;
        activeElement?.blur();
        setTimeout(handleShowWarningModal, 700);
      } else {
        handleShowWarningModal();
      }
      return;
    }
    onPress();
  }, [showWarnings, shouldShowWarningModalOnPress, onPress, isKeyboardOpen, handleShowWarningModal]);
  const onAcceptTokenWarning = useCallback(() => {
    setShowWarningModal(false);
    onPress();
  }, [onPress]);
  return <TokenOptionItemContextMenu
    actions={CONTEXT_MENU_ACTIONS["tokenSelector" /* TokenSelector */]}
    currency={currency}
    isOpen={isContextMenuOpen}
    closeMenu={closeContextMenu}
  >
      <TouchableArea
    animation="300ms"
    width="100%"
    opacity={showWarnings && severity === WarningSeverity.Blocked || isUnsupported ? 0.5 : 1}
    hoverStyle={{ backgroundColor: "$surface1Hovered" }}
    onPress={onPressTokenOption}
    onLongPress={openContextMenu}
  >
        {isWeb ? (
    // eslint-disable-next-line react/forbid-elements
    <div onContextMenu={openContextMenu}>
            <LegacyBaseTokenOptionItem {...props} />
          </div>
  ) : <LegacyBaseTokenOptionItem {...props} />}
      </TouchableArea>

      <TokenWarningModal
    currencyInfo0={currencyInfo}
    isVisible={showWarningModal}
    closeModalOnly={() => setShowWarningModal(false)}
    onAcknowledge={onAcceptTokenWarning}
  />
    </TokenOptionItemContextMenu>;
}
function isLegacyTokenOptionItemProps(props) {
  return "balance" in props;
}
const BaseTokenOptionItem = memo(function _BaseTokenOptionItem(props) {
  const {
    option,
    onPress,
    showTokenAddress,
    rightElement,
    showDisabled,
    modalInfo,
    focusedRowControl,
    openContextMenu
  } = props;
  const { currencyInfo } = option;
  const { currency } = currencyInfo;
  const severity = getTokenWarningSeverity(currencyInfo);
  const { colorSecondary: warningIconColor } = getWarningIconColors(severity);
  return <OptionItem
    image={<TokenLogo
      chainId={currency.chainId}
      name={currency.name}
      symbol={currency.symbol}
      url={currencyInfo.logoUrl ?? void 0}
    />}
    title={currency.name ?? currency.symbol ?? ""}
    subtitle={<Flex row alignItems="center" gap="$spacing8">
          <Text color="$neutral2" numberOfLines={1} variant="body3">
            {getSymbolDisplayText(currency.symbol)}
          </Text>
          {!currency.isNative && showTokenAddress && <Flex shrink>
              <Text color="$neutral3" numberOfLines={1} variant="body3">
                {shortenAddress(currency.address)}
              </Text>
            </Flex>}
        </Flex>}
    badge={<Flex row alignItems="center" gap="$spacing4">
          {isTokenVerified(currency) && <BlueBadgeVerified size={"$icon.16"} />}
          {warningIconColor && <Flex>
              <WarningIcon severity={severity} size="$icon.16" strokeColorOverride={warningIconColor} />
            </Flex>}
        </Flex>}
    rightElement={rightElement}
    disabled={showDisabled}
    testID={`token-option-${currency.chainId}-${currency.symbol}`}
    modalInfo={modalInfo}
    focusedRowControl={focusedRowControl}
    onPress={onPress}
    onLongPress={openContextMenu}
  />;
});
export const TokenOptionItem = memo(function _TokenOptionItem(props) {
  const { value: isContextMenuOpen, setFalse: closeContextMenu, setTrue: openContextMenu } = useBooleanState(false);
  if (!isLegacyTokenOptionItemProps(props)) {
    return <TokenOptionItemContextMenu
      actions={CONTEXT_MENU_ACTIONS[props.contextMenuVariant]}
      currency={props.option.currencyInfo.currency}
      isOpen={isContextMenuOpen}
      closeMenu={closeContextMenu}
    >
        {
      /* eslint-disable-next-line react/forbid-elements */
    }
        <div onContextMenu={openContextMenu}>
          <BaseTokenOptionItem {...props} />
        </div>
      </TokenOptionItemContextMenu>;
  }
  return <_LegacyTokenOptionItem {...props} />;
});
